import { BarChart3, Camera, ChefHat, Dumbbell, Mic, ScanLine, Sparkles, Target, UtensilsCrossed } from "lucide-react";
import { AppShot } from "./AppShot";

const VALUE_PILLARS = [
  {
    Icon: UtensilsCrossed,
    eyebrow: "Nutrition",
    title: "Fuel with less friction",
    copy: "Log meals by search, barcode or Pro Photo Scan, repeat saved meals, track water, and keep calories, macros and fiber in view.",
    details: ["Multi-database food search", "Pro Photo Scan with editable estimates", "Custom foods and recipes", "Meal diary and hydration"],
  },
  {
    Icon: Dumbbell,
    eyebrow: "Training",
    title: "Train with context",
    copy: "Follow a program or start an empty workout, then log sets with prior performance, rest timing and progression close at hand.",
    details: ["Programs and quick workouts", "Exercise library and demos", "Sets, PRs and workout history"],
  },
  {
    Icon: BarChart3,
    eyebrow: "Progress",
    title: "See the signal",
    copy: "Connect bodyweight direction, training consistency and strength history instead of treating every number as an isolated result.",
    details: ["Goal-aware weight progress", "Training volume and frequency", "Optional Pro interpretation"],
  },
];

const SCREENS = [
  {
    id: "today",
    label: "Today",
    title: "Your next useful action",
    copy: "Food, training and daily progress meet in one calm home screen.",
    src: "/screenshots/aktua-today.webp",
    alt: "AKTUA Today dashboard",
  },
  {
    id: "nutrition",
    label: "Nutrition",
    title: "A practical food diary",
    copy: "Search, scan a barcode or use Pro Photo Scan, then review the day without duplicate summaries.",
    src: "/screenshots/aktua-nutrition.webp",
    alt: "AKTUA Nutrition diary with four meal sections",
  },
  {
    id: "train",
    label: "Train",
    title: "Ready for the gym floor",
    copy: "Start quickly, repeat recent sessions or move into your program and exercise library.",
    src: "/screenshots/aktua-train.webp",
    alt: "AKTUA Train screen with quick workout and recent workouts",
  },
  {
    id: "recipes",
    label: "Recipes",
    title: "Meals worth making",
    copy: "Browse 30 complete free recipes, then open the broader Pro catalog when you want more.",
    src: "/screenshots/aktua-recipes.webp",
    alt: "AKTUA Recipes screen with recipe collections and nutrition details",
  },
];

export const Features = () => (
  <section id="features" className="scroll-mt-20 bg-[#FBFAF5] py-20 lg:py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="reveal max-w-3xl">
        <p className="eyebrow text-[#2F7555]">The launch experience</p>
        <h2 className="section-title mt-3 text-4xl text-[#202622] sm:text-5xl lg:text-6xl">
          Everything needed to build consistency. Nothing fighting for attention.
        </h2>
        <p className="mt-5 max-w-2xl text-base font-medium leading-relaxed text-[#65706A] md:text-lg">
          AKTUA keeps the daily loop simple: decide what matters, log it quickly, and use the history to make the next decision better.
        </p>
      </div>

      <div className="mt-12 grid gap-5 lg:grid-cols-3">
        {VALUE_PILLARS.map(({ Icon, eyebrow, title, copy, details }, index) => (
          <article
            key={title}
            data-testid={`value-pillar-${index + 1}`}
            className="reveal rounded-[1.75rem] border border-[#D8D1C5] bg-[#F7F3EA] p-7 shadow-[0_18px_45px_-34px_rgba(4,31,26,0.35)]"
            style={{ transitionDelay: `${index * 90}ms` }}
          >
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#E2EFE8] text-[#2F7555]">
              <Icon size={21} aria-hidden="true" />
            </span>
            <p className="eyebrow mt-6 text-[#3F8F68]">{eyebrow}</p>
            <h3 className="mt-2 text-2xl font-bold tracking-[-0.025em] text-[#202622]">{title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#65706A]">{copy}</p>
            <ul className="mt-6 space-y-2 border-t border-[#D8D1C5] pt-5">
              {details.map((detail) => (
                <li key={detail} className="flex items-center gap-2 text-sm font-bold text-[#34413B]">
                  <Target size={14} className="shrink-0 text-[#3F8F68]" aria-hidden="true" />
                  {detail}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>

      <div className="reveal relative mt-6 overflow-hidden rounded-[2rem] bg-[#041F1A] p-7 shadow-[0_24px_70px_-42px_rgba(4,31,26,0.8)] sm:p-9 lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-12">
        <div className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full bg-[#3F8F68]/20 blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#8BE8C7] text-[#041F1A]">
              <Camera size={23} aria-hidden="true" />
            </span>
            <div>
              <p className="eyebrow text-[#8BE8C7]">AKTUA Pro</p>
              <p className="mt-1 text-sm font-bold text-[#F3F7F5]">Photo Scan</p>
            </div>
          </div>
          <h3 className="mt-6 max-w-2xl text-3xl font-bold tracking-[-0.035em] text-[#F3F7F5] sm:text-4xl">
            Photograph a meal. Review the estimate. Log it when it looks right.
          </h3>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#B8C8C3]">
            Take or choose a food photo, add a typed or voice description, then edit the approximate calories, macros, fiber and sodium before saving.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Approximate by design", "20 successful estimates a day", "Nothing logged automatically"].map((item) => (
              <span key={item} className="rounded-full border border-[#8BE8C7]/20 bg-[#8BE8C7]/[0.07] px-3 py-2 text-xs font-bold text-[#BFEEDD]">{item}</span>
            ))}
          </div>
        </div>

        <div className="relative mt-8 rounded-3xl border border-[#8BE8C7]/15 bg-[#062B24] p-5 lg:mt-0" aria-label="Photo Scan workflow">
          {[
            { Icon: Camera, step: "01", title: "Take or choose a photo", copy: "Start with the meal in front of you." },
            { Icon: Mic, step: "02", title: "Add useful context", copy: "Type it or transcribe a voice description." },
            { Icon: Sparkles, step: "03", title: "Review and edit", copy: "Confirm the estimate before it reaches your diary." },
          ].map(({ Icon, step, title, copy }, index) => (
            <div key={step} className={`flex gap-4 py-4 ${index ? "border-t border-[#8BE8C7]/10" : "pt-1"}`}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#8BE8C7]/10 text-[#8BE8C7]">
                <Icon size={18} aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-[#6EA995]">Step {step}</p>
                <p className="mt-1 font-bold text-[#F3F7F5]">{title}</p>
                <p className="mt-1 text-sm leading-relaxed text-[#9FB4AE]">{copy}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="reveal mt-20 flex items-end justify-between gap-8">
        <div className="max-w-2xl">
          <p className="eyebrow text-[#2F7555]">Inside the app</p>
          <h2 className="section-title mt-3 text-3xl text-[#202622] sm:text-4xl">A focused screen for each part of the routine.</h2>
        </div>
        <div className="hidden items-center gap-2 text-sm font-bold text-[#2F7555] md:flex">
          <ScanLine size={18} aria-hidden="true" /> Current AKTUA screens
        </div>
      </div>

      <div className="screenshot-rail mt-10" data-testid="product-screenshot-gallery">
        {SCREENS.map((screen, index) => (
          <article key={screen.id} className="reveal screenshot-card" style={{ transitionDelay: `${index * 70}ms` }}>
            <AppShot src={screen.src} alt={screen.alt} className={screen.id === "train" ? "app-shot--train" : ""} />
            <div className="px-1 pt-5">
              <p className="eyebrow text-[#3F8F68]">{screen.label}</p>
              <h3 className="mt-2 text-lg font-bold text-[#202622]">{screen.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#65706A]">{screen.copy}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="reveal mt-10 rounded-3xl border border-[#C7DED3] bg-[#EAF3ED] p-6 sm:flex sm:items-center sm:justify-between sm:gap-8">
        <div>
          <p className="eyebrow text-[#2F7555]">Recipes included</p>
          <p className="mt-2 text-lg font-bold text-[#202622]">30 complete recipes are part of Free.</p>
          <p className="mt-1 text-sm leading-relaxed text-[#65706A]">Ten high-protein, ten vegetarian and ten ingredient-screened allergen-aware recipes.</p>
        </div>
        <div className="mt-5 flex shrink-0 items-center gap-2 text-sm font-bold text-[#2F7555] sm:mt-0">
          <ChefHat size={19} aria-hidden="true" /> 19,000+ recipes with Pro
        </div>
      </div>
    </div>
  </section>
);
